import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/useAuth';
import {
  notificationReadService,
  notificationCommandService,
  subscribeToNotificationInserts,
} from '@/modules/notification';

export function useNotifications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['notifications', user?.id],
    queryFn: () => notificationReadService.getNotifications(user!.id),
    enabled: !!user,
    staleTime: 30_000,
  });

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['notifications-unread-count', user?.id],
    queryFn: () => notificationReadService.getUnreadCount(user!.id),
    enabled: !!user,
    staleTime: 30_000,
  });

  // Real-time subscription for new notifications
  useEffect(() => {
    if (!user) return;

    return subscribeToNotificationInserts(user.id, () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', user.id] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count', user.id] });
    });
  }, [user, queryClient]);

  const markAsRead = useMutation({
    mutationFn: (notificationId: string) =>
      notificationCommandService.markAsRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count', user?.id] });
    },
  });

  const markAllAsRead = useMutation({
    mutationFn: () => notificationCommandService.markAllAsRead(user!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count', user?.id] });
    },
  });

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead: markAsRead.mutate,
    markAllAsRead: markAllAsRead.mutate,
  };
}

